<template>
    <section class="association-section">
        <div class="row">
            <div class="col-lg-12">
                <div class="ibox">
                    <div class="ibox-title">
                        <h5>{{ association.title }}</h5>
                    </div>
                    <div class="ibox-content">
                        <div class="row mb-2">
                            <div class="col-md-12">
                                <div class="bulk-actions d-flex align-items-center">
                                    <div class="mr-10">
                                        <!-- Only show add/attach buttons for one-to-many or many-to-many relationships -->
                                        <template v-if="!isOneToOneWithExistingEntry">
                                            <button class="btn btn-primary mr-1" data-toggle="modal" :data-target="'#AddModal-' + entityIdentifier">
                                                <i class="fa fa-plus mr-05"></i> Add New {{ association.title }}
                                            </button>
                                            <button class="btn btn-secondary" data-toggle="modal" :data-target="'#addExistingModal-' + associationId" @click="fetchExistingEntities">
                                                <i class="fa fa-link mr-05"></i> Attach Existing {{ association.title }}
                                            </button>
                                        </template>
                                        <!-- Show message for one-to-one relationships that already have an entry -->
                                        <div v-else-if="startingEntities && startingEntities.length" class="alert alert-info mb-0">
                                            <i class="fa fa-info-circle mr-1"></i>
                                            This {{ entity.form_name.toLowerCase() }} can only be associated with one {{ association.title.toLowerCase() }}.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-12">
                                <!-- Show table only if there are entities -->
                                <table class="table facility-single-table table-striped sortable-table" v-if="startingEntities && startingEntities.length">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th v-for="(headerLabel, headerKey) in tableHeaders" :key="headerKey">
                                                {{ headerLabel }}
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="entry in startingEntities" :key="entry.id" :class="{ '-deactivated': entry.deactivated }">
                                            <td><span class="badge badge-default">{{ entry.id }}</span></td>
                                            
                                            <td v-for="(value, key) in entry.display_attributes" :key="key">
                                                {{ formatDisplayAttribute(value, key) }}
                                            </td>

                                            <td>
                                                <div class="btn-group">
                                                    <a :href="entry.link" class="btn-white btn-edit-entry btn btn-xs">
                                                        <i class="fa fa-pencil mr-05" aria-hidden="true"></i>View/Edit
                                                    </a>
                                                    <button class="btn-white btn btn-xs btn-delete" @click="detachEntity(entry.id)" :disabled="isDetaching === entry.id">
                                                        <i class="fa" :class="{'fa-chain-broken': isDetaching !== entry.id, 'fa-spinner fa-spin': isDetaching === entry.id}" aria-hidden="true"></i>
                                                        {{ isDetaching === entry.id ? 'Detaching...' : 'Detach' }}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                
                                <!-- Placeholder message when no entities exist -->
                                <div class="text-center p-5 bg-light rounded" v-else>
                                    <p class="mb-0 text-muted mt-3 mb-3">
                                        <i class="fa fa-info-circle mr-1"></i>
                                        No {{ association.title }} associated yet.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Attaching Existing Entities -->
        <div class="modal fade" :id="'addExistingModal-' + associationId" tabindex="-1" role="dialog" :aria-labelledby="'addExistingModalLabel-' + associationId" aria-hidden="true">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title d-ib" id="addExistingModalLabel">Attach Existing {{ association.title }}</h5>
                        <button type="button" class="close d-ib" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <!-- For one-to-one relationships, only allow selecting one at a time -->
                        <div v-if="isOneToOne" class="alert alert-info mb-3">
                            <i class="fa fa-info-circle mr-1"></i>
                            This {{ entity.form_name.toLowerCase() }} can only be associated with one {{ association.title.toLowerCase() }} at a time.
                        </div>
                        
                        <table class="table table-striped" v-if="existingEntities && existingEntities.length">
                            <thead>
                                <tr>
                                    <th>
                                        <!-- Add Select All checkbox in the header -->
                                        <input 
                                            type="checkbox" 
                                            :checked="isAllSelected"
                                            @change="toggleSelectAll"
                                            :disabled="isOneToOne && selectedEntities.length > 0"
                                            title="Select All"
                                        >
                                    </th>
                                    <th v-for="header in getTableHeaders(existingEntities)" :key="header">
                                        {{ header }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="entry in existingEntities" :key="entry.id" :class="{ 'table-success': isSelected(entry.id), '-deactivated': entry.deactivated }">
                                    <td>
                                        <input 
                                            type="checkbox" 
                                            :value="entry.id" 
                                            v-model="selectedEntities"
                                            @change="handleSelectionChange(entry.id)"
                                            :disabled="isOneToOne && selectedEntities.length > 0 && !isSelected(entry.id)"
                                        >
                                    </td>
                                    <td v-for="(value, key) in entry.display_attributes" :key="key">
                                        {{ formatDisplayAttribute(value, key) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-center align-items-center h-100" v-else-if="isLoading">
                            <i class="fa fa-spinner fa-spin"></i>
                        </div>
                        <div class="alert alert-info" v-else>
                            <i class="fa fa-info-circle mr-1"></i>
                            No {{ association.title }} available to attach.
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
                        <button type="button" class="btn btn-primary" @click="attachSelectedEntities">Add {{ association.title }}</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

export default {
    props: {
        association: {
            type: Object,
            required: true
        },
        entity: {
            type: Object,
            required: true
        },
        associationKey: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isUpdating: false,
            isDetaching: null,
            isLoading: null,
            existingEntities: [],
            selectedEntities: [], // Stores selected machine IDs
            startingEntities: [],  // Local copy of association.entries
            componentUid: 'assoc_' + Math.random().toString(36).substring(2, 9), // Generate a random component ID
            result: null
        };
    },
    mounted() {
        // Deep clone association.entries when component mounts
        this.startingEntities = cloneDeep(this.association.entries);
    },
    methods: {
        toggleSelectAll(event) {
            const isChecked = event.target.checked;
            
            if (isChecked) {
                // For one-to-one relationships, only select the first entity
                if (this.isOneToOne && this.existingEntities.length > 0) {
                    this.selectedEntities = [this.existingEntities[0].id];
                } else {
                    // For many-to-many relationships, select all entities
                    this.selectedEntities = this.existingEntities.map(entry => entry.id);
                }
            } else {
                // Unselect all
                this.selectedEntities = [];
            }
        },
        fetchExistingEntities() {
            this.isLoading = true;
            
            // Use a mapping object instead of a switch statement
            const roleTypeMap = {
                'physician': 'physician',
                'aide': 'aide',
                'technologists': 'tech',
                'medical_director': 'medical_director'
            };
            
            // Get the role type from the mapping
            let roleType = null;
            if (this.association.form_key === 'staff' && this.associationKey in roleTypeMap) {
                roleType = roleTypeMap[this.associationKey];
            }
            
            $.ajax({
                url: localized.resturl + '/company/get-entities',
                method: 'GET',
                beforeSend: function(xhr) {
                    xhr.setRequestHeader('X-WP-Nonce', localized.restnonce);
                },
                dataType: 'json',
                data: {
                    company_id: this.entity.company_id,
                    form_key: this.association.form_key,
                    entity_id: this.entity.id,
                    role_type: roleType
                },
                success: (result) => {
                    this.isLoading = false;
                    
                    if (result.success && result.data && result.data.entries) {
                        this.existingEntities = result.data.entries;
                        this.initializeSelection();
                    } else {
                        toastr.error(result.message || 'Failed to load entities', 'Error');
                    }
                },
                error: (xhr, status, error) => {
                    this.isLoading = false;
                    toastr.error('Failed to fetch existing entities: ' + error, 'Error');
                }
            });
        },

        initializeSelection() {
            // Use the startingEntities to determine which checkboxes should be checked
            // (these are the entities already associated with the facility)
            this.selectedEntities = this.startingEntities.map(entry => entry.id);
            
            // If there are no existing entities, we need to make sure there's no confusion
            if (!this.existingEntities || !this.existingEntities.length) {
                return;
            }
        },

        // Handle checkbox selection for one-to-one relationships
        handleSelectionChange(id) {
            // For one-to-one relationships, ensure only one entity is selected
            if (this.isOneToOne && this.selectedEntities.includes(id)) {
                // If this item was just checked, uncheck all others
                this.selectedEntities = [id];
            }
        },

        isSelected(id) {
            return this.selectedEntities.includes(id);
        },

        detachEntity(id) {
            // Set the ID of the entity being detached to show spinner
            this.isDetaching = id;
            
            // Call the new dedicated endpoint
            $.ajax({
                url: localized.resturl + '/company/detach-association',
                method: 'POST',
                beforeSend: function(xhr) {
                    xhr.setRequestHeader('X-WP-Nonce', localized.restnonce);
                },
                dataType: 'json',
                data: {
                    entity_id: this.entity.id,
                    child_id: id,
                    association_type: this.association.form_key
                },
                success: (result) => {
                    if (result.success) {
                        // Remove from local arrays on success
                        this.startingEntities = this.startingEntities.filter(entry => entry.id !== id);
                        this.selectedEntities = this.selectedEntities.filter(selectedId => selectedId !== id);
                        toastr.success(result.message, 'Success');
                    } else {
                        toastr.error(result.message, 'Error');
                    }
                    
                    // Clear detaching state
                    this.isDetaching = null;
                },
                error: (xhr, status, error) => {
                    toastr.error('Failed to detach entity: ' + error, 'Error');
                    this.isDetaching = null;
                }
            });
        },

        attachSelectedEntities() {
            this.isUpdating = true;
            
            // For one-to-one relationships, limit to one selection
            const entriesToAttach = this.isOneToOne && this.selectedEntities.length > 1 
                ? [this.selectedEntities[0]] 
                : this.selectedEntities;
            
            $.ajax({
                url: localized.resturl + '/company/update-associations',
                method: 'POST',
                beforeSend: function(xhr) {
                    xhr.setRequestHeader('X-WP-Nonce', localized.restnonce);
                },
                dataType: 'json',
                data: {
                    entity_id: this.entity.id,
                    association_type: this.association.form_key, // 'machines', 'staff', etc.
                    entry_ids: entriesToAttach
                },
                success: (result) => {
                    if (result.success) {
                        // Find the selected entities from existingEntities
                        const newSelections = this.existingEntities.filter(entity =>
                            entriesToAttach.includes(entity.id)
                        );

                        // Update local copy
                        this.startingEntities = _.cloneDeep(newSelections);
                        
                        toastr.success(result.message, 'Success');
                    } else {
                        toastr.error(result.message, 'Error');
                    }
                    
                    // Close the modal
                    $('#' + 'addExistingModal-' + this.associationId).modal('hide');
                    this.isUpdating = false;
                },
                error: (xhr, status, error) => {
                    toastr.error('Failed to update associations: ' + error, 'Error');
                    this.isUpdating = false;
                }
            });
        },

        getTableHeaders(entries) {
            if (!entries || !entries.length || !entries[0].display_attributes) {
                return {};
            }
            return Object.keys(entries[0].display_attributes).reduce((formattedHeaders, key) => {
                const formattedKey = key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
                formattedHeaders[key] = formattedKey;
                return formattedHeaders;
            }, {});
        },

        formatDisplayAttribute(value, key) {
            // Special handling for role field
            if (key === 'role' && Array.isArray(value)) {
                // Map role keys to user-friendly display names
                const roleMap = {
                    'physician': 'Supervising Physician',
                    'tech': 'Remote Technologist',
                    'ctaide': 'CT Aide',
                    'mriaide': 'MRI Aide',
                    'medical_director': 'Medical Director'
                };
                
                return value.map(role => roleMap[role] || role.charAt(0).toUpperCase() + role.slice(1)).join(', ');
            }
            
            // Default handling for other field types
            if (!this.notEmpty(value)) return '--';
            
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            
            return value;
        },

        notEmpty(val) {
            if (val == null) return false;
            if (typeof val === 'string') return val.trim() !== '';
            if (Array.isArray(val)) return val.length > 0;
            if (typeof val === 'object') return Object.keys(val).length > 0;
            return Boolean(val);
        }
    },
    computed: {
        isAllSelected() {
            if (!this.existingEntities || !this.existingEntities.length) return false;
            return this.selectedEntities.length === this.existingEntities.length;
        },
        // Check if this is a one-to-one relationship
        isOneToOne() {
            return this.association.type === 'one-to-one';
        },
        
        // Check if this is a one-to-one relationship with existing entries
        isOneToOneWithExistingEntry() {
            return this.isOneToOne && 
                this.startingEntities && 
                this.startingEntities.length > 0 && 
                this.startingEntities.some(entry => entry !== null);
        },
        
        tableHeaders() {
            return this.getTableHeaders(this.startingEntities);
        },
        entityIdentifier() {
            return this.association.form_key;
        },
        associationId() {
            return this.componentUid;
        }
    }
};
</script>